/**
 * Team Design — persistence for team name + uploaded logo.
 *
 * The collection page lets shoppers type a team name and upload a logo before
 * picking a product. Both must survive a page refresh AND follow the shopper
 * across our domains — the storefront is at `squadmojo.com` while the design
 * tools and customer portal live on `app.squadmojo.com`. localStorage is
 * per-origin, so it can't bridge those subdomains — we use a cookie scoped
 * to the parent domain (`Domain=.squadmojo.com`) as the single source of
 * truth that every subdomain reads and writes.
 *
 * Storage shape (cookie: 'team_design'):
 *   {
 *     teamName: '<string>',
 *     logo:     <VisitorUpload result>   // urls.{thumbnail,medium,original}, mediaId, ...
 *   }
 *
 * Exposes window.TeamDesignStorage — used by:
 *  - team-design.text-render.js  (restore state on load)
 *  - team-design.text.js         (save name on input)
 *  - team-design.logo-upload.js  (save / clear logo)
 *
 * Must load before the files above.
 */
(function () {
  'use strict';

  var COOKIE_NAME    = 'team_design';
  var COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

  // Mirrors @3d/frontend/utils `getDomain()` so storefront and design-tools
  // write to the same cookie scope. ".<root>.tld" makes the cookie visible to
  // every subdomain — that's the whole reason we use a cookie here.
  function getCookieDomain() {
    if (typeof location === 'undefined') return '';
    var hostname = location.hostname;
    var parts = hostname.split('.');
    if (parts.length < 2) return ''; // localhost / single-label host
    if (/\.(dev|qa)\.truetale\.io$/i.test(hostname)) {
      return '.' + parts.slice(-3).join('.');
    }
    return '.' + parts.slice(-2).join('.');
  }

  function readCookie() {
    if (typeof document === 'undefined') return null;
    var match = document.cookie.match(
      new RegExp('(?:^|; )' + COOKIE_NAME + '=([^;]*)'),
    );
    if (!match) return null;
    try {
      return JSON.parse(decodeURIComponent(match[1]));
    } catch (e) {
      return null;
    }
  }

  function writeCookie(value) {
    if (typeof document === 'undefined') return;
    var encoded = encodeURIComponent(JSON.stringify(value));
    var attrs = 'path=/; max-age=' + COOKIE_MAX_AGE + '; SameSite=Lax';
    var domain = getCookieDomain();
    if (domain) attrs += '; domain=' + domain;
    if (typeof location !== 'undefined' && location.protocol === 'https:') {
      attrs += '; Secure';
    }
    document.cookie = COOKIE_NAME + '=' + encoded + '; ' + attrs;
  }

  window.TeamDesignStorage = {
    get: function () {
      return readCookie() || {};
    },
    setName: function (name) {
      var d = this.get();
      d.teamName = name;
      writeCookie(d);
    },
    setLogo: function (result) {
      var d = this.get();
      d.logo = result;
      writeCookie(d);
    },
    clearLogo: function () {
      var d = this.get();
      delete d.logo;
      writeCookie(d);
    },
  };
})();
